/* eslint-disable prettier/prettier */
/* signIn.ts */
import { toast } from "react-toastify";
import { call, put } from "redux-saga/effects";
import store from "../.."; // sua store
import axiosServicesLogin from "../../../services/axiosServicesLogin";
import { setLoadingLogin, setLoginIsErrorMessage, setUser } from "./actions";
import { AuthState } from "./types";
import { Response } from "../../../interfaces";
import {
  TOKEN_KEY,
  TOKEN_EXPIRATION_KEY,
} from "../../../hooks/useRefreshToken";

const instance = axiosServicesLogin; // Instância de axios configurada p/ login

/**
 * Saga responsável pelo processo de login
 *
 * Fluxo:
 * 1. Ativa loading
 * 2. Troca token Azure AD pelo token interno da API
 * 3. Atualiza estado na Redux store
 * 4. Salva tokens no localStorage
 * 5. Atualiza Redux e finaliza loading
 */
export function* signIn({
  payload,
}: {
  payload: {
    token: string; // tokenAd (do MSAL)
    idAD?: string; // opcional, pode vir do MSAL
  };
}): any {
  try {
    const { token, idAD } = payload;

    // 1) Ativa loading
    yield put(setLoadingLogin(true));

    // 2) Chama a API para trocar tokenAd pelo token interno
    const response: Response = yield call(
      instance.post,
      "identity/app-web/conta-usuario/v1/gerar-token",
      { tokenAd: token }
    );

    // Validação da resposta
    if (!response?.data?.token?.access_token) {
      throw new Error("Token inválido na resposta da API");
    }

    // 3) Monta objeto AuthState para o Redux
    const data: AuthState = {
      idAD: idAD || response?.data?.usuario?.idAD || null,
      userId: response?.data?.usuario?.userId,
      name: response?.data?.usuario?.name,
      apelido: response?.data?.usuario?.apelido,
      username: response?.data?.usuario?.username,
      urlFoto: response?.data?.usuario?.urlFoto,
      siglaNome: response?.data?.usuario?.siglaNome,
      termosCondicoesRealizacaoAgendamentosAceito:
        response?.data?.usuario?.termosCondicoesRealizacaoAgendamentosAceito,
      termosClubeVantagensPolePositionAceito:
        response?.data?.usuario?.termosClubeVantagensPolePositionAceito,
      deviceId: response?.data?.usuario?.deviceId,
      codigoRegional365: response?.data?.usuario?.codigoRegional365,
      idRegional: response?.data?.usuario?.idRegional,
      idRegionalSistemaOrigem: response?.data?.usuario?.idRegionalSistemaOrigem,
      idCorretorSistemaOrigem: response?.data?.usuario?.idCorretorSistemaOrigem,
      idCorretor2013: response?.data?.usuario?.idCorretor2013,
      usuarioVivaz: response?.data?.usuario?.usuarioVivaz,
      usuarioBackofficeVivaz:
        response?.data?.usuario?.usuarioBackofficeVivaz ?? false,
      usuarioComercial: response?.data?.usuario?.usuarioComercial,
      usuarioCia: response?.data?.usuario?.usuarioCia,
      usuarioDiretor: response?.data?.usuario?.usuarioDiretor || false,
      usuarioGerenteOnline:
        response?.data?.usuario?.usuarioGerenteOnline || false,
      permissoes: response?.data?.usuario?.permissoes,
      empresaVendas: response?.data?.usuario?.empresaVendas,
      idsBandeiras: response.data.usuario.idsBandeiras || [],
      consumerApi: response?.data?.usuario?.consumerApi,
      idPerfilAcesso365: response?.data?.usuario?.idPerfilAcesso365,
      idEmpresaVendas365: response?.data?.usuario?.idEmpresaVendas365,
      codigoEmpresaVendas365: response?.data?.usuario?.codigoEmpresaVendas365,
      plantaoVendas:
        response?.data?.usuario?.plantaoVendas === null
          ? null
          : {
              id: response?.data?.usuario?.plantaoVendas?.id,
              nome: response?.data?.usuario?.plantaoVendas?.nome,
              empreendimentoPDV:
                response?.data?.usuario?.plantaoVendas?.empreendimentoPDV ===
                null
                  ? null
                  : {
                      id: response?.data?.usuario?.plantaoVendas
                        ?.empreendimentoPDV?.id,
                      nome: response?.data?.usuario?.plantaoVendas
                        ?.empreendimentoPDV?.nome,
                    },
            },
      acessoAppFase1: response?.data?.usuario?.acessoAppFase1,
      acessoAppFase2: response?.data?.usuario?.acessoAppFase2,
      acessoAdmin: response?.data?.usuario?.acessoAdmin,
      acessoMobile: response?.data?.usuario?.acessoMobile,
      acessoAppWeb: response?.data?.usuario?.acessoAppWeb,
      idEquipeCargo: response?.data?.usuario?.idEquipeCargo,
      idCorretorBolsao365: response?.data?.usuario?.idCorretorBolsao365,
      idCargo: response?.data?.usuario?.idCargo,
      idCargo2013: response?.data?.usuario?.idCargo2013,
      codigoCargo2013: response?.data?.usuario?.codigoCargo2013,
      token: response?.data?.token?.access_token,
      isError: false,
      signed: true,
      loading: false,
      dataNascimento: response?.data?.usuario?.dataNascimento,
      dataVencimentoCreci: response?.data?.usuario?.dataVencimentoCreci,
      fazAniversarioHoje: response?.data?.usuario?.fazAniversarioHoje,
      creciVencido: response?.data?.usuario?.creciVencido,
      supervisorProduto: response?.data?.usuario?.supervisorProduto,
      possuiToken3cplus: response?.data?.possuiToken3cplus || false,
      linkToken3cplus: response?.data?.linkToken3cplus || "",
    };

    // 4) Salva token e refreshToken no localStorage (para uso nos interceptors)
    const tokenData = {
      refreshToken: response?.data?.token?.refresh_token,
      token: response?.data?.token?.access_token, // token interno
      tokenAD: token, // mantemos token do AD se precisar
      expires_in: response?.data?.token?.expires_in,
    };

    // Calcula quando expira e salva
    const expirationDate = new Date();
    const expiresInSeconds = parseInt(
      tokenData.expires_in?.substring(0, tokenData.expires_in.length - 1),
      10
    );
    expirationDate.setSeconds(expirationDate.getSeconds() + expiresInSeconds);

    // Salva no localStorage usando constantes para chaves
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
    localStorage.setItem(TOKEN_EXPIRATION_KEY, JSON.stringify(expirationDate));

    console.log("[Auth] Login bem-sucedido, token salvo");

    // 5) Atualiza Redux
    yield put(setUser(data));
    yield put(setLoadingLogin(false));

    // Limpa flags de verificação de token para forçar nova verificação
    sessionStorage.removeItem("@token-checked");
    sessionStorage.removeItem("@appbar-token-checked");
    sessionStorage.removeItem("@token-health-check-done");
  } catch (err: any) {
    console.error("[Auth] Erro no processo de login:", err);

    // Tratamento de erro específico
    if (err?.status === 500) {
      yield put(setLoginIsErrorMessage(true));
      toast.error("Erro interno no servidor, tente novamente mais tarde.");
    } else {
      const errorMessages = err?.response?.data?.notifications
        ?.map((item: any) => item?.message)
        .filter(Boolean)
        .join("\n");
      toast.error(
        errorMessages || "Não foi possível realizar o login. Tente novamente."
      );
      yield put(setLoginIsErrorMessage(true));
    }

    yield put(setLoadingLogin(false));
  }
}
